import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Ban, ChevronDown, ChevronUp, Check, Package, Plus, Ticket } from "lucide-react";
import { TicketCatalogManager } from "./TicketCatalogManager";
import { generateEventTitleAndDescription, getDefaultEventSettings, getEventFormatConfig, normalizeEventSettings } from "@/lib/eventSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const isEventCancelled = (event) => ["cancelled", "canceled"].includes(String(event?.status || "").toLowerCase());

const getAuthConfig = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const INITIAL_EVENT_FORM = {
  title: "", description: "", date: "", time: "", end_time: "",
  venue_id: "", location: "", price: "", tickets_available: "", image_url: "",
  show_capacity: false,
  event_type: "presencial",
  event_format: "padrao", quiz_type: "Variedades",
  thematic_round_theme: "", special_title: "", special_rounds: ["", "", "", ""],
  title_auto_mode: true,
  is_recurring: false, recurrence_interval: 1, recurrence_unit: "weeks",
  recurrence_end_date: "", series_id: null, copy_last_prize: false,
  online_start_datetime: "", online_end_datetime: "",
  is_streaming: false, streaming_link: "",
  streaming_mode: "external_link",
  quiz_maker_quiz_id: "", quiz_maker_quiz_title: "",
  kahoot_pin: "",
  eligible_plans: ["free", "pro", "master"],
  quiz_category_id: "", quiz_themes: [], quiz_question_count: 10,
  quiz_use_difficulty_filter: false, quiz_difficulties: [],
  quiz_time_per_question: 30, prize_description: "",
  requires_ticket: false, requires_game_pass: false, game_pass_cost: 1, free_plans: [], ticket_sales_paused: false,
  selected_ticket_type_ids: [],
  ticket_tiers: [],
};

const DIFFICULTY_OPTIONS = [
  { value: "easy", label: "Fácil" },
  { value: "medium", label: "Médio" },
  { value: "hard", label: "Difícil" },
];

const generateTitleAndDescription = (form) => {
  const { event_format, quiz_type, thematic_round_theme, special_title, special_rounds } = form;
  let title = "";
  let description = "";
  const theme = thematic_round_theme?.trim();
  const quizTypeDescriptions = {
    Variedades: "História, Geografia, Ciências, Mundo Animal, Música, Cinema, etc",
    "Cultura Geek": "Séries, Desenhos Animados, Quadrinhos, Games e Cinema",
    "Cultura Pop": "Cinema e TV, Conhecimentos Gerais e Música",
  };
  const quizTypeDescription = quizTypeDescriptions[quiz_type];

  if (event_format === "fandom") {
    title = `Quiz Fandom: 4 rodadas de ${theme || "[Tema]"}`;
    description = `4 rodadas exclusivas sobre ${theme || "[Tema]"}.`;
  } else if (event_format === "especial") {
    title = `Quiz Especial: ${special_title || "[Título]"}`;
    const roundsList = special_rounds?.filter((r) => r?.trim()).join(", ");
    description = roundsList
      ? `Quiz Especial com 4 rodadas temáticas: ${roundsList}.`
      : "Quiz Especial com 4 rodadas temáticas.";
  } else {
    title = theme ? `${quiz_type} + ${theme}` : `${quiz_type} + Rodada Temática`;
    if (quizTypeDescription) {
      description = theme
        ? `3 Rodadas de ${quiz_type} (${quizTypeDescription}) + Rodada temática de ${theme}.`
        : `3 Rodadas de ${quiz_type} (${quizTypeDescription}) + Rodada temática a definir.`;
    } else {
      description = theme
        ? `3 Rodadas de ${quiz_type} + Rodada temática de ${theme}.`
        : `3 Rodadas de ${quiz_type} + Rodada temática a definir.`;
    }
  }
  return { title, description };
};

const normalizeDateTimeForInput = (value) => {
  if (!value) return "";
  return String(value).slice(0, 16);
};

const formatCurrencyValue = (value) => {
  const normalized = Number.parseFloat(value);
  return Number.isFinite(normalized) ? normalized.toFixed(2) : "0.00";
};

const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .filter(Boolean)
      .join(" | ") || fallback;
  }
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") {
    return detail.message || JSON.stringify(detail);
  }
  return fallback;
};

const normalizeEventTierForForm = (tier) => ({
  ...tier,
  start_date: normalizeDateTimeForInput(tier.start_date),
  end_date: normalizeDateTimeForInput(tier.end_date),
  end_before_value: tier.end_before_value ?? null,
  end_before_unit: tier.end_before_unit || "",
  end_before_reference: tier.end_before_reference || "",
  start_after_lot: tier.start_after_lot || "",
  visibility: tier.visibility || "available",
});

const normalizeQuizMakerOption = (quiz, sourceScope) => ({
  ...quiz,
  question_count: Number.parseInt(quiz?.question_count, 10) || quiz?.questions?.length || 0,
  source_scope: sourceScope,
});

const mergeQuizMakerOptions = (adminQuizzes = [], myQuizzes = []) => {
  const merged = new Map();

  adminQuizzes.forEach((quiz) => {
    if (!quiz?.id) return;
    merged.set(quiz.id, normalizeQuizMakerOption(quiz, "admin"));
  });

  myQuizzes.forEach((quiz) => {
    if (!quiz?.id) return;
    const existing = merged.get(quiz.id);
    merged.set(
      quiz.id,
      normalizeQuizMakerOption(
        {
          ...existing,
          ...quiz,
          source_scope: existing ? "both" : "mine",
        },
        existing ? "both" : "mine",
      ),
    );
  });

  return Array.from(merged.values()).sort((left, right) => {
    const leftRank = left.source_scope === "mine" || left.source_scope === "both" ? 0 : 1;
    const rightRank = right.source_scope === "mine" || right.source_scope === "both" ? 0 : 1;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return String(left.title || "").localeCompare(String(right.title || ""), "pt-BR", { sensitivity: "base" });
  });
};

const getQuizMakerSourceLabel = (quiz) => {
  if (quiz?.source_scope === "both") return "Meus Quizzes + Biblioteca";
  if (quiz?.source_scope === "mine") return "Meus Quizzes";
  return "Biblioteca Admin";
};

const formatQuizMakerOptionLabel = (quiz) => {
  const sourceLabel = getQuizMakerSourceLabel(quiz);
  const statusLabel = quiz?.status ? ` - ${quiz.status}` : "";
  return `[${sourceLabel}] ${quiz?.title || "Quiz sem titulo"} (${quiz?.question_count || 0} perguntas${statusLabel})`;
};

export function EventEditorDialog({ open, onOpenChange, event = null, onSaved = () => {} }) {
  const [eventForm, setEventForm] = useState({ ...INITIAL_EVENT_FORM });
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventSettings, setEventSettings] = useState(getDefaultEventSettings());
  const [venues, setVenues] = useState([]);
  const [ticketCatalog, setTicketCatalog] = useState([]);
  const [quizMakerQuizzes, setQuizMakerQuizzes] = useState([]);
  const [quizCategoriesThemes, setQuizCategoriesThemes] = useState([]);
  const [availableThemes, setAvailableThemes] = useState([]);
  const [specialThemes, setSpecialThemes] = useState([]);
  const [expandedTicketTypes, setExpandedTicketTypes] = useState({});
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const onOpenChangeRef = useRef(onOpenChange);
  const currentFormatConfig = getEventFormatConfig(eventSettings, eventForm.event_format);
  const availableEventTypes = currentFormatConfig?.types || [];
  const isDigitalProduct = eventForm.event_type === "online";
  const myQuizMakerQuizzes = useMemo(
    () => (quizMakerQuizzes || []).filter((quiz) => quiz?.source_scope === "mine" || quiz?.source_scope === "both"),
    [quizMakerQuizzes],
  );
  const adminQuizMakerQuizzes = useMemo(
    () => (quizMakerQuizzes || []).filter((quiz) => quiz?.source_scope === "admin"),
    [quizMakerQuizzes],
  );

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  const resetState = useCallback(() => {
    const defaultEventSettings = getDefaultEventSettings();
    setEventSettings(defaultEventSettings);
    setEventForm({
      ...INITIAL_EVENT_FORM,
      ...generateEventTitleAndDescription(INITIAL_EVENT_FORM, defaultEventSettings),
    });
    setEditingEvent(null);
    setAvailableThemes([]);
    setExpandedTicketTypes({});
  }, []);

  const fetchTicketCatalog = useCallback(async () => {
    const res = await axios.get(`${API}/ticket-catalog`, getAuthConfig());
    const data = res.data || [];
    setTicketCatalog(data);
    return data;
  }, []);

  const fetchSupportData = useCallback(async () => {
    const [venuesRes, categoriesRes, themesRes, catalog, settingsRes, quizMakerAdminRes, quizMakerMineRes] = await Promise.all([
      axios.get(`${API}/venues`),
      axios.get(`${API}/quiz/categories-themes`),
      axios.get(`${API}/sheets/all-themes`),
      fetchTicketCatalog(),
      axios.get(`${API}/cms/settings`),
      axios.get(`${API}/quiz/community-quizzes/admin`, getAuthConfig()).catch((error) => {
        console.error("Erro ao carregar biblioteca admin do Quiz Maker:", error);
        return { data: { quizzes: [] } };
      }),
      axios.get(`${API}/quiz/community-quizzes`, getAuthConfig()).catch((error) => {
        console.error("Erro ao carregar Meus Quizzes:", error);
        return { data: { quizzes: [] } };
      }),
    ]);
    const normalizedEventSettings = normalizeEventSettings(settingsRes.data?.eventSettings || getDefaultEventSettings());
    const mergedQuizMakerQuizzes = mergeQuizMakerOptions(
      quizMakerAdminRes.data?.quizzes || [],
      quizMakerMineRes.data?.quizzes || [],
    );
    setEventSettings(normalizedEventSettings);
    setVenues((venuesRes.data || []).filter((v) => v.is_active !== false));
    setQuizMakerQuizzes(mergedQuizMakerQuizzes);
    setQuizCategoriesThemes(categoriesRes.data || []);
    setSpecialThemes(themesRes.data?.special_themes || []);
    return {
      catalog,
      categories: categoriesRes.data || [],
      quizMakerQuizzes: mergedQuizMakerQuizzes,
      venues: venuesRes.data || [],
      eventSettings: normalizedEventSettings,
    };
  }, [fetchTicketCatalog]);

  const fetchLastPrize = useCallback(async (venueId) => {
    if (!venueId) return;
    try {
      const res = await axios.get(`${API}/events/venue/${venueId}/last-prize`);
      if (res.data?.prize_description) {
        setEventForm((prev) => ({ ...prev, prize_description: res.data.prize_description }));
        toast.success("Premiação copiada do último evento!");
      }
    } catch (error) {
      console.error("Error fetching last prize:", error);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingForm(true);
      try {
        const { catalog, categories, eventSettings: loadedEventSettings } = await fetchSupportData();
        if (cancelled) return;

        if (event?.id) {
          const res = await axios.get(`${API}/events/${event.id}`, getAuthConfig());
          if (cancelled) return;
          const fullEvent = res.data;
          const normalizedEventType =
            fullEvent.event_type
            || event?.event_type
            || (
              fullEvent.quiz_maker_quiz_id
              || fullEvent.is_streaming === true
              || fullEvent.streaming_mode === "embedded_room"
              ? "online"
              : "presencial"
            );
          const normalizedStreamingMode =
            fullEvent.streaming_mode
            || event?.streaming_mode
            || (fullEvent.quiz_maker_quiz_id ? "embedded_room" : "external_link");
          const normalizedIsStreaming =
            fullEvent.is_streaming === true
            || event?.is_streaming === true
            || normalizedStreamingMode === "embedded_room";
          let selectedIds = [...(fullEvent.selected_ticket_type_ids || [])];
          let ticketTiers = (fullEvent.ticket_tiers || []).map((tier) => normalizeEventTierForForm({ ...tier }));

          if (ticketTiers.length > 0 && ticketTiers.some((tier) => !tier.catalog_ticket_type_id)) {
            ticketTiers = ticketTiers.map((tier) => {
              if (tier.catalog_ticket_type_id) return tier;
              const tierName = tier.name || "";
              const baseName = tierName.includes(" - ")
                ? tierName.split(" - ").slice(0, -1).join(" - ")
                : tierName;
              const match = catalog.find((tt) => tt.name === baseName);
              return match ? { ...tier, catalog_ticket_type_id: match.id, catalog_ticket_type_name: match.name } : tier;
            });
          }

          if (selectedIds.length === 0 && ticketTiers.length > 0) {
            selectedIds = [...new Set(ticketTiers.filter((tier) => tier.catalog_ticket_type_id).map((tier) => tier.catalog_ticket_type_id))];
          }

          setEditingEvent(fullEvent);
          setEventForm({
            ...INITIAL_EVENT_FORM,
            ...fullEvent,
            event_type: normalizedEventType,
            is_streaming: normalizedIsStreaming,
            streaming_mode: normalizedIsStreaming ? normalizedStreamingMode : "external_link",
            event_format: fullEvent.event_format || loadedEventSettings.formats?.[0]?.id || INITIAL_EVENT_FORM.event_format,
            quiz_type: fullEvent.quiz_type || loadedEventSettings.formats?.[0]?.types?.[0]?.label || INITIAL_EVENT_FORM.quiz_type,
            venue_id: fullEvent.venue_id || "",
            thematic_round_theme: fullEvent.thematic_round_theme || "",
            special_title: fullEvent.special_title || "",
            special_rounds: fullEvent.special_rounds || ["", "", "", ""],
            title_auto_mode: false,
            prize_description: fullEvent.award || fullEvent.prize_description || "",
            series_id: fullEvent.series_id || null,
            quiz_maker_quiz_id: fullEvent.quiz_maker_quiz_id || "",
            quiz_maker_quiz_title: fullEvent.quiz_maker_quiz_title || "",
            kahoot_pin: fullEvent.kahoot_pin || "",
            quiz_use_difficulty_filter: fullEvent.quiz_use_difficulty_filter === true,
            quiz_difficulties: Array.isArray(fullEvent.quiz_difficulties) ? fullEvent.quiz_difficulties : [],
            selected_ticket_type_ids: selectedIds,
            ticket_tiers: ticketTiers,
            requires_game_pass: normalizedEventType === "online"
              ? Boolean(fullEvent.requires_game_pass ?? fullEvent.requires_ticket ?? ticketTiers.length > 0)
              : false,
            game_pass_cost: Math.max(1, Number.parseInt(fullEvent.game_pass_cost, 10) || 1),
          });

          if (fullEvent.quiz_category_id) {
            const category = categories.find((item) => item.id === fullEvent.quiz_category_id);
            setAvailableThemes(category?.themes || []);
          }
        } else {
          const initialForm = {
            ...INITIAL_EVENT_FORM,
            event_format: loadedEventSettings.formats?.[0]?.id || INITIAL_EVENT_FORM.event_format,
            quiz_type: loadedEventSettings.formats?.[0]?.types?.[0]?.label || INITIAL_EVENT_FORM.quiz_type,
          };
          setEventForm({ ...initialForm, ...generateEventTitleAndDescription(initialForm, loadedEventSettings) });
          setEditingEvent(null);
          setAvailableThemes([]);
          setExpandedTicketTypes({});
        }
      } catch (error) {
        console.error("Error loading event editor:", error);
        toast.error("Erro ao carregar editor do evento");
        onOpenChangeRef.current?.(false);
      } finally {
        if (!cancelled) setLoadingForm(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [event?.event_type, event?.id, event?.is_streaming, event?.streaming_mode, fetchSupportData, open, resetState]);

  const handleEventFormChange = (updates) => {
    setEventForm((prev) => {
      const next = { ...prev, ...updates };
      if ("event_type" in updates && updates.event_type === "online") {
        next.venue_id = "";
        next.location = "";
        next.copy_last_prize = false;
      }
      if ("is_streaming" in updates && updates.is_streaming === true) {
        next.online_start_datetime = "";
        next.online_end_datetime = "";
        next.quiz_category_id = "";
        next.quiz_themes = [];
        next.quiz_use_difficulty_filter = false;
        next.quiz_difficulties = [];
        next.streaming_mode = next.streaming_mode || "external_link";
      }
      if ("is_streaming" in updates && updates.is_streaming === false) {
        next.streaming_mode = "external_link";
        next.quiz_maker_quiz_id = "";
        next.quiz_maker_quiz_title = "";
        next.kahoot_pin = "";
      }
      if ("event_format" in updates) {
        const nextFormat = getEventFormatConfig(eventSettings, updates.event_format);
        next.quiz_type = nextFormat?.types?.[0]?.label || "";
        if (nextFormat?.themeMode === "hidden") {
          next.thematic_round_theme = "";
        }
        if (!nextFormat?.supportsSpecialTitle) {
          next.special_title = "";
        }
        if (!nextFormat?.supportsSpecialRounds) {
          next.special_rounds = ["", "", "", ""];
        }
      }
      const affectsGeneration = ["event_format", "quiz_type", "thematic_round_theme", "special_title", "special_rounds"].some((key) => key in updates);
      if (affectsGeneration) {
        const generated = generateEventTitleAndDescription(next, eventSettings);
        next.title = generated.title;
        next.description = generated.description;
        next.title_auto_mode = true;
      }
      return next;
    });
  };

  const handleVenueChange = (venueId) => {
    const venue = venues.find((item) => item.id === venueId);
    const shouldCopyPrize = eventForm.copy_last_prize;
    setEventForm((prev) => ({
      ...prev,
      venue_id: venueId,
      location: venue?.address || venue?.name || "",
      tickets_available: venue?.capacity || prev.tickets_available,
    }));
    if (venueId && shouldCopyPrize) fetchLastPrize(venueId);
  };

  const handleCopyLastPrizeChange = (checked) => {
    setEventForm((prev) => ({ ...prev, copy_last_prize: checked }));
    if (checked && eventForm.venue_id) fetchLastPrize(eventForm.venue_id);
  };

  const handleQuizCategoryChange = (categoryId) => {
    const category = quizCategoriesThemes.find((item) => item.id === categoryId);
    setAvailableThemes(category?.themes || []);
    setEventForm((prev) => ({ ...prev, quiz_category_id: categoryId, quiz_themes: [] }));
  };

  const handleThemeToggle = (themeName) => {
    setEventForm((prev) => ({
      ...prev,
      quiz_themes: prev.quiz_themes?.includes(themeName)
        ? prev.quiz_themes.filter((theme) => theme !== themeName)
        : [...(prev.quiz_themes || []), themeName],
    }));
  };

  const handleDifficultyToggle = (difficulty) => {
    setEventForm((prev) => ({
      ...prev,
      quiz_difficulties: prev.quiz_difficulties?.includes(difficulty)
        ? prev.quiz_difficulties.filter((item) => item !== difficulty)
        : [...(prev.quiz_difficulties || []), difficulty],
    }));
  };

  const handleQuizMakerQuizSelect = (quizId) => {
    const selectedQuiz = (quizMakerQuizzes || []).find((quiz) => quiz.id === quizId);
    setEventForm((prev) => ({
      ...prev,
      quiz_maker_quiz_id: quizId,
      quiz_maker_quiz_title: selectedQuiz?.title || "",
    }));
  };

  const handlePlanToggle = (plan) => {
    setEventForm((prev) => ({
      ...prev,
      eligible_plans: prev.eligible_plans?.includes(plan)
        ? prev.eligible_plans.filter((item) => item !== plan)
        : [...(prev.eligible_plans || []), plan],
      free_plans: prev.eligible_plans?.includes(plan)
        ? (prev.free_plans || []).filter((item) => item !== plan)
        : (prev.free_plans || []),
    }));
  };

  const handleFreePlanToggle = (plan) => {
    setEventForm((prev) => ({
      ...prev,
      eligible_plans: prev.eligible_plans?.includes(plan)
        ? (prev.eligible_plans || [])
        : [...(prev.eligible_plans || []), plan],
      free_plans: (prev.free_plans || []).includes(plan)
        ? prev.free_plans.filter((item) => item !== plan)
        : [...(prev.free_plans || []), plan],
    }));
  };

  const convertCatalogLotsToEventTiers = (catalogTicketType) => {
    const lots = (catalogTicketType.lots || []).map((lot, idx) => ({
      catalog_ticket_type_id: catalogTicketType.id,
      catalog_ticket_type_name: catalogTicketType.name,
      catalog_lot_id: lot.id || null,
      name: `${catalogTicketType.name} - ${lot.name || `${idx + 1}º Lote`}`,
      price: lot.price || catalogTicketType.base_price,
      payment_provider: catalogTicketType.payment_provider || "auto",
      use_custom_fee: catalogTicketType.use_custom_fee === true,
      fee_percentage: lot.fee_percentage || catalogTicketType.fee_percentage || 5,
      fee_fixed: lot.fee_fixed || catalogTicketType.fee_fixed || 0.40,
      min_quantity: catalogTicketType.min_qty || 1,
      purchase_max_quantity: catalogTicketType.max_qty || null,
      max_quantity: lot.quantity_limit || null,
      start_date: lot.starts_at ? normalizeDateTimeForInput(lot.starts_at) : "",
      end_date: lot.ends_at ? normalizeDateTimeForInput(lot.ends_at) : "",
      end_before_value: lot.end_before_value || null,
      end_before_unit: lot.end_before_unit || "",
      end_before_reference: lot.end_before_reference || "",
      start_after_lot: lot.start_after_lot || "",
      is_active: lot.is_active !== false,
      visibility: lot.visibility || catalogTicketType.visibility || "available",
    }));

    return lots.length > 0 ? lots : [{
      catalog_ticket_type_id: catalogTicketType.id,
      catalog_ticket_type_name: catalogTicketType.name,
      catalog_lot_id: null,
      name: catalogTicketType.name,
      price: catalogTicketType.base_price,
      payment_provider: catalogTicketType.payment_provider || "auto",
      use_custom_fee: catalogTicketType.use_custom_fee === true,
      fee_percentage: catalogTicketType.fee_percentage || 5,
      fee_fixed: catalogTicketType.fee_fixed || 0.40,
      min_quantity: catalogTicketType.min_qty || 1,
      purchase_max_quantity: catalogTicketType.max_qty || null,
      max_quantity: catalogTicketType.capacity || null,
      start_date: "",
      end_date: "",
      is_active: true,
      visibility: catalogTicketType.visibility || "available",
    }];
  };

  const handleToggleTicketType = (ticketType) => {
    setEventForm((prev) => {
      const currentIds = prev.selected_ticket_type_ids || [];
      const currentTiers = prev.ticket_tiers || [];
      const isSelected = currentIds.includes(ticketType.id);

      if (isSelected) {
        return {
          ...prev,
          selected_ticket_type_ids: currentIds.filter((id) => id !== ticketType.id),
          ticket_tiers: currentTiers.filter((tier) => tier.catalog_ticket_type_id !== ticketType.id),
        };
      }

      return {
        ...prev,
        selected_ticket_type_ids: [...currentIds, ticketType.id],
        ticket_tiers: currentTiers.some((tier) => tier.catalog_ticket_type_id === ticketType.id)
          ? currentTiers
          : [...currentTiers, ...convertCatalogLotsToEventTiers(ticketType)],
      };
    });
  };

  const handleLocalTicketCreate = (formData) => {
    const localId = `local_${Date.now()}`;
    setEventForm((prev) => ({
      ...prev,
      ticket_tiers: [
        ...(prev.ticket_tiers || []),
        ...(formData.lots?.length
          ? formData.lots.map((lot, idx) => ({
              local_ticket_id: localId,
              local_ticket_name: formData.name,
              name: `${formData.name} - ${lot.name || `${idx + 1}º Lote`}`,
              price: lot.price ?? formData.base_price,
              payment_provider: formData.payment_provider || "auto",
              use_custom_fee: formData.use_custom_fee === true,
              fee_percentage: lot.fee_percentage || formData.fee_percentage || 5,
              fee_fixed: lot.fee_fixed || formData.fee_fixed || 0.40,
              min_quantity: formData.min_qty || 1,
              purchase_max_quantity: formData.max_qty || null,
              max_quantity: lot.quantity_limit || null,
              start_date: normalizeDateTimeForInput(lot.starts_at),
              end_date: normalizeDateTimeForInput(lot.ends_at),
              end_before_value: lot.end_before_value || null,
              end_before_unit: lot.end_before_unit || "",
              end_before_reference: lot.end_before_reference || "",
              start_after_lot: lot.start_after_lot || "",
              visibility: lot.visibility || formData.visibility || "available",
            }))
          : [{
              local_ticket_id: localId,
              local_ticket_name: formData.name,
              name: formData.name,
              price: formData.base_price,
              payment_provider: formData.payment_provider || "auto",
              use_custom_fee: formData.use_custom_fee === true,
              fee_percentage: formData.fee_percentage || 5,
              fee_fixed: formData.fee_fixed || 0.40,
              min_quantity: formData.min_qty || 1,
              purchase_max_quantity: formData.max_qty || null,
              max_quantity: null,
              start_date: "",
              end_date: "",
              visibility: formData.visibility || "available",
            }]),
      ],
    }));
  };

  const handleTicketTierChange = (index, field, value) => {
    setEventForm((prev) => ({
      ...prev,
      ticket_tiers: prev.ticket_tiers.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier)),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!eventForm.title?.trim()) {
        toast.error("Preencha o título do evento.");
        return;
      }
      if (!eventForm.description?.trim()) {
        toast.error("Preencha a descrição do evento.");
        return;
      }
      if (!eventForm.date) {
        toast.error("Preencha a data do evento.");
        return;
      }
      if (!eventForm.time) {
        toast.error("Preencha o horário de início.");
        return;
      }
      if (currentFormatConfig?.themeMode === "required" && !eventForm.thematic_round_theme?.trim()) {
        toast.error("Esse formato exige o preenchimento do tema.");
        return;
      }
      if (eventForm.event_type === "online" && !eventForm.is_streaming) {
        if (!eventForm.online_start_datetime || !eventForm.online_end_datetime) {
          toast.error("Preencha o período disponível para jogar.");
          return;
        }
      }

      const hasTickets =
        (eventForm.selected_ticket_type_ids || []).length > 0
        || (eventForm.ticket_tiers || []).length > 0
        || eventForm.requires_ticket;
      const requiresGamePass = isDigitalProduct
        ? Boolean(eventForm.requires_game_pass || eventForm.requires_ticket || hasTickets)
        : false;

      const normalizedQuestionCount = Number.parseInt(eventForm.quiz_question_count, 10);
      const normalizedTimePerQuestion = Number.parseInt(eventForm.quiz_time_per_question, 10);
      const selectedQuizMakerQuiz = (quizMakerQuizzes || []).find((quiz) => quiz.id === eventForm.quiz_maker_quiz_id);

      if (
        eventForm.event_type === "online"
        && eventForm.is_streaming
        && eventForm.streaming_mode === "embedded_room"
        && !eventForm.quiz_maker_quiz_id
        && !eventForm.kahoot_pin?.trim()
      ) {
        toast.error("Selecione o quiz base do Quiz Maker ou informe o PIN do Kahoot para a sala incorporada.");
        return;
      }

      const payload = {
        ...eventForm,
        price: parseFloat(eventForm.price) || 0,
        tickets_available: parseInt(eventForm.tickets_available) || 0,
        venue_id: eventForm.venue_id || null,
        end_time: eventForm.end_time || null,
        show_capacity: eventForm.show_capacity || false,
        online_start_datetime: eventForm.is_streaming ? null : (eventForm.online_start_datetime || null),
        online_end_datetime: eventForm.is_streaming ? null : (eventForm.online_end_datetime || null),
        is_streaming: eventForm.is_streaming || false,
        streaming_link: eventForm.streaming_link || null,
        quiz_maker_quiz_id: eventForm.is_streaming && eventForm.streaming_mode === "embedded_room"
          ? (eventForm.quiz_maker_quiz_id || null)
          : null,
        quiz_maker_quiz_title: eventForm.is_streaming && eventForm.streaming_mode === "embedded_room"
          ? (selectedQuizMakerQuiz?.title || eventForm.quiz_maker_quiz_title || null)
          : null,
        kahoot_pin: eventForm.is_streaming && eventForm.streaming_mode === "embedded_room"
          ? (eventForm.kahoot_pin?.trim() || null)
          : null,
        eligible_plans: eventForm.eligible_plans || ["free", "pro", "master"],
        quiz_category_id: eventForm.is_streaming ? null : (eventForm.quiz_category_id || null),
        quiz_themes: eventForm.is_streaming ? [] : (eventForm.quiz_themes || []),
        quiz_use_difficulty_filter: eventForm.is_streaming ? false : (eventForm.quiz_use_difficulty_filter === true),
        quiz_difficulties: eventForm.is_streaming ? [] : (eventForm.quiz_use_difficulty_filter ? (eventForm.quiz_difficulties || []) : []),
        quiz_question_count: eventForm.is_streaming ? 10 : (Number.isFinite(normalizedQuestionCount) ? normalizedQuestionCount : 10),
        quiz_time_per_question: eventForm.is_streaming ? 30 : (Number.isFinite(normalizedTimePerQuestion) ? normalizedTimePerQuestion : 30),
        event_format: eventForm.event_format || "padrao",
        quiz_type: eventForm.quiz_type || "Variedades",
        thematic_round_theme: eventForm.thematic_round_theme || null,
        special_title: eventForm.special_title || null,
        special_rounds: eventForm.special_rounds?.some((round) => round?.trim()) ? eventForm.special_rounds : null,
        title_auto_mode: eventForm.title_auto_mode,
        prize_description: eventForm.prize_description || null,
        streaming_mode: eventForm.is_streaming ? (eventForm.streaming_mode || "external_link") : "external_link",
        requires_ticket: isDigitalProduct ? false : hasTickets,
        requires_game_pass: requiresGamePass,
        game_pass_cost: requiresGamePass ? Math.max(1, Math.min(100, Number.parseInt(eventForm.game_pass_cost, 10) || 1)) : 1,
        free_plans: (eventForm.free_plans || []).filter((plan) => (eventForm.eligible_plans || ["free", "pro", "master"]).includes(plan)),
        selected_ticket_type_ids: isDigitalProduct ? [] : (eventForm.selected_ticket_type_ids || []),
        ticket_tiers: !isDigitalProduct && hasTickets
          ? (eventForm.ticket_tiers || []).map((tier) => ({
              ...tier,
              price: parseFloat(tier.price) || 0,
              max_quantity: tier.max_quantity ? parseInt(tier.max_quantity) : null,
              start_date: tier.start_date || null,
              end_date: tier.end_date || null,
              end_before_value: tier.end_before_value ? parseInt(tier.end_before_value) : null,
            }))
          : null,
      };

      delete payload.use_custom_tickets;

      if (eventForm.venue_id) {
        const selectedVenue = venues.find((venue) => venue.id === eventForm.venue_id);
        if (selectedVenue) payload.location = selectedVenue.address || selectedVenue.name;
      }

      if (editingEvent?.id) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload, getAuthConfig());
        toast.success("Evento atualizado com sucesso!");
      } else if (eventForm.is_recurring && eventForm.recurrence_end_date) {
        const recurrence = {
          interval: parseInt(eventForm.recurrence_interval, 10) || 1,
          unit: eventForm.recurrence_unit || "weeks",
          end_date: eventForm.recurrence_end_date,
        };
        const res = await axios.post(`${API}/events/recurrence`, payload, {
          params: recurrence,
          ...getAuthConfig(),
        });
        toast.success(`${res.data.created} eventos criados com sucesso!`);
      } else {
        await axios.post(`${API}/events`, payload, getAuthConfig());
        toast.success("Evento criado com sucesso!");
      }

      onSaved();
      onOpenChange(false);
      resetState();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error(getApiErrorMessage(error, "Erro ao salvar evento"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEvent = async () => {
    if (!editingEvent?.id || cancelling) return;
    if (!window.confirm("Tem certeza que deseja cancelar este evento? Ele continuará visível, mas ficará marcado como CANCELADO e não aceitará novas compras.")) return;

    setCancelling(true);
    try {
      const response = await axios.post(`${API}/events/${editingEvent.id}/cancel`, {}, getAuthConfig());
      toast.success("Evento cancelado.");
      if ((response.data?.cancellation_summary?.errors || []).length > 0) {
        toast.warning("O evento foi cancelado, mas alguns pedidos precisam de revisão manual.");
      }
      onSaved();
      onOpenChange(false);
      resetState();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível cancelar o evento."));
    } finally {
      setCancelling(false);
    }
  };

  const renderCatalogTicketEditor = (ticketType) => {
    const isSelected = (eventForm.selected_ticket_type_ids || []).includes(ticketType.id);
    const isExpanded = expandedTicketTypes[ticketType.id];
    const tiersForThisType = (eventForm.ticket_tiers || []).filter((tier) => tier.catalog_ticket_type_id === ticketType.id);

    return (
      <div key={ticketType.id} className={`rounded-lg border transition-colors ${isSelected ? "border-[#fccf1f]/50 bg-[#fccf1f]/5" : "border-zinc-700 bg-zinc-900/30 hover:border-zinc-600"}`}>
        <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => handleToggleTicketType(ticketType)}>
          <div className="flex items-center gap-3">
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? "bg-[#fccf1f] border-[#fccf1f]" : "border-zinc-600"}`}>
              {isSelected && <Check className="w-3 h-3 text-black" />}
            </div>
            <div>
              <p className="text-white font-medium">{ticketType.name}</p>
              <p className="text-zinc-400 text-xs">Base: R$ {formatCurrencyValue(ticketType.base_price)} · {ticketType.lots?.length || 1} lote(s)</p>
            </div>
          </div>
          {isSelected && tiersForThisType.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedTicketTypes((prev) => ({ ...prev, [ticketType.id]: !prev[ticketType.id] }));
              }}
              className="text-zinc-400 hover:text-white"
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
          )}
        </div>
        {isSelected && isExpanded && tiersForThisType.length > 0 && (
          <div className="px-3 pb-3 border-t border-zinc-700/50 pt-3 space-y-2">
            <p className="text-zinc-400 text-xs font-medium flex items-center gap-1"><Package size={12} /> Lotes de "{ticketType.name}"</p>
            {tiersForThisType.map((tier) => {
              const globalIdx = (eventForm.ticket_tiers || []).findIndex((item) => item === tier);
              return (
                <div key={`${ticketType.id}-${globalIdx}`} className="bg-zinc-800/40 border border-zinc-700 rounded-lg p-3 space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div><Label className="text-zinc-500 text-xs">Nome</Label><Input type="text" value={tier.name} onChange={(e) => handleTicketTierChange(globalIdx, "name", e.target.value)} className="bg-zinc-900 border-zinc-700 text-white h-7 text-xs" /></div>
                    <div><Label className="text-zinc-500 text-xs">Preço (R$)</Label><Input type="number" step="0.01" value={tier.price} onChange={(e) => handleTicketTierChange(globalIdx, "price", parseFloat(e.target.value) || 0)} className="bg-zinc-900 border-zinc-700 text-white h-7 text-xs" /></div>
                    <div><Label className="text-zinc-500 text-xs">Limite Qtd.</Label><Input type="number" value={tier.max_quantity || ""} onChange={(e) => handleTicketTierChange(globalIdx, "max_quantity", e.target.value ? parseInt(e.target.value) : null)} className="bg-zinc-900 border-zinc-700 text-white h-7 text-xs" placeholder="ilimitado" /></div>
                    <div><Label className="text-zinc-500 text-xs">Fim (Data)</Label><Input type="datetime-local" value={tier.end_date || ""} onChange={(e) => handleTicketTierChange(globalIdx, "end_date", e.target.value)} className="bg-zinc-900 border-zinc-700 text-white h-7 text-xs" /></div>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Provedor de pagamento</Label>
                    <select value={tier.payment_provider || "auto"} onChange={(e) => handleTicketTierChange(globalIdx, "payment_provider", e.target.value)} className="mt-1 w-full bg-zinc-900 border border-zinc-700 text-white rounded px-2 h-7 text-xs">
                      <option value="auto">Automático: Asaas no presencial e InfinitePay no digital</option>
                      <option value="infinitepay">InfinitePay</option>
                      <option value="asaas">Asaas (mínimo de R$ 5,00 no total final)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input type="number" min="0" value={tier.end_before_value || ""} onChange={(e) => handleTicketTierChange(globalIdx, "end_before_value", e.target.value || null)} className="bg-zinc-900 border-zinc-700 text-white h-7 text-xs" placeholder="Número" />
                    <select value={tier.end_before_unit || ""} onChange={(e) => handleTicketTierChange(globalIdx, "end_before_unit", e.target.value)} className="bg-zinc-900 border border-zinc-700 text-white rounded px-2 h-7 text-xs">
                      <option value="">Unidade</option>
                      <option value="minutes">Minutos</option>
                      <option value="hours">Horas</option>
                      <option value="days">Dias</option>
                    </select>
                    <select value={tier.end_before_reference || ""} onChange={(e) => handleTicketTierChange(globalIdx, "end_before_reference", e.target.value)} className="bg-zinc-900 border border-zinc-700 text-white rounded px-2 h-7 text-xs">
                      <option value="">Antes de...</option>
                      <option value="event_start">Início do evento</option>
                      <option value="event_end">Fim do evento</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Virar lote quando esgotar</Label>
                    <select value={tier.start_after_lot || ""} onChange={(e) => handleTicketTierChange(globalIdx, "start_after_lot", e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 text-white rounded px-2 h-7 text-xs">
                      <option value="">Nenhum (usar data de início)</option>
                      {(eventForm.ticket_tiers || []).filter((candidate, idx) => idx !== globalIdx).map((candidate, idx) => (
                        <option key={`${candidate.name}-${idx}`} value={candidate.name}>{candidate.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };
  return (
    <>
      <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) resetState(); onOpenChange(nextOpen); }}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-nunito font-bold text-2xl text-[#fccf1f]">
              {editingEvent ? "Editar Evento" : "Criar Novo Evento"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">Preencha os dados do evento</DialogDescription>
          </DialogHeader>

          {loadingForm ? (
            <div className="py-8 text-center text-zinc-400">Carregando editor do evento...</div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                <Label className="text-white font-semibold">Modalidade</Label>
                <select
                  value={eventForm.event_type}
                  onChange={(e) => handleEventFormChange({ event_type: e.target.value })}
                  className="w-full bg-zinc-900/80 border border-white/10 text-white rounded-md px-3 py-2"
                >
                  <option value="presencial">Presencial</option>
                  <option value="online">Jogo Online</option>
                </select>
              </div>

              {eventForm.event_type === "online" && (
                <div className="rounded-xl border border-[#fccf1f]/20 bg-[#fccf1f]/5 p-4 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#fccf1f]">Quiz Maker</p>
                      <h3 className="mt-1 text-lg font-black text-white">Integração do jogo ao vivo</h3>
                      <p className="mt-1 text-sm text-zinc-300">
                        Configure aqui se este evento usa um quiz fechado do Quiz Maker como base da sala incorporada.
                      </p>
                    </div>
                    <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-right">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Status atual</p>
                      <p className="mt-1 text-sm font-bold text-white">
                        {eventForm.is_streaming
                          ? (eventForm.streaming_mode === "embedded_room" ? "Jogo incorporado no site" : "Streaming com link externo")
                          : "Jogo online sem streaming"}
                      </p>
                    </div>
                  </div>

                  {eventForm.is_streaming && eventForm.streaming_mode === "embedded_room" ? (
                    <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-4 py-3">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-300">Jogo vinculado</p>
                      <p className="mt-1 text-sm text-white">
                        {eventForm.quiz_maker_quiz_title || (eventForm.kahoot_pin ? `Kahoot PIN ${eventForm.kahoot_pin}` : "Nenhum quiz do Quiz Maker ou PIN do Kahoot selecionado ainda.")}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-white/15 bg-black/20 px-4 py-3">
                      <p className="text-sm text-zinc-300">
                        Para liberar a seleção do Quiz Maker, marque <strong>Ao vivo / Streaming</strong> e escolha <strong>Jogo incorporado no site</strong> mais abaixo.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-zinc-800/50 p-4 rounded-lg space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Formato do Evento</Label>
                    <select value={eventForm.event_format} onChange={(e) => handleEventFormChange({ event_format: e.target.value })} className="w-full bg-zinc-900/80 border border-white/10 text-white rounded-md px-3 py-2">
                      {(eventSettings.formats || []).map((format) => (
                        <option key={format.id} value={format.id}>{format.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-white">{currentFormatConfig?.typeLabel || "Tipo do Evento"}</Label>
                    <select value={eventForm.quiz_type} onChange={(e) => handleEventFormChange({ quiz_type: e.target.value })} className="w-full bg-zinc-900/80 border border-white/10 text-white rounded-md px-3 py-2">
                      {availableEventTypes.map((type) => (
                        <option key={type.id} value={type.label}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {currentFormatConfig?.themeMode !== "hidden" && (
                  <div>
                    <Label className="text-white">
                      {currentFormatConfig?.themeLabel || "Tema"}
                      {currentFormatConfig?.themeMode === "required" && <span className="text-red-400"> *</span>}
                    </Label>
                    <Input
                      type="text"
                      value={eventForm.thematic_round_theme}
                      onChange={(e) => handleEventFormChange({ thematic_round_theme: e.target.value })}
                      className="bg-zinc-900/80 border-white/10 text-white"
                      placeholder={currentFormatConfig?.themeMode === "required" ? "Preencha o tema deste evento" : "Opcional - pode definir depois"}
                      required={currentFormatConfig?.themeMode === "required"}
                    />
                  </div>
                )}
                {currentFormatConfig?.supportsSpecialTitle && (
                  <div>
                    <Label className="text-white">{currentFormatConfig?.specialTitleLabel || "Título Especial"}</Label>
                    <Input
                      type="text"
                      value={eventForm.special_title}
                      onChange={(e) => handleEventFormChange({ special_title: e.target.value })}
                      className="bg-zinc-900/80 border-white/10 text-white"
                      placeholder="Ex: Grandes trilhas sonoras"
                    />
                  </div>
                )}
                {currentFormatConfig?.supportsSpecialRounds && (
                  <div className="space-y-2">
                    <Label className="text-white">{currentFormatConfig?.specialRoundsLabel || "Rodadas Especiais"}</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {eventForm.special_rounds.map((round, index) => (
                        <Input
                          key={`special-round-${index}`}
                          type="text"
                          value={round}
                          onChange={(e) => {
                            const nextRounds = [...eventForm.special_rounds];
                            nextRounds[index] = e.target.value;
                            handleEventFormChange({ special_rounds: nextRounds });
                          }}
                          className="bg-zinc-900/80 border-white/10 text-white"
                          placeholder={`Rodada ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-white">Título</Label>
                  <Input type="text" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value, title_auto_mode: false })} className="bg-zinc-900/80 border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-white">Descrição</Label>
                  <Input type="text" value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value, title_auto_mode: false })} className="bg-zinc-900/80 border-white/10 text-white" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div><Label className="text-white">Data</Label><Input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white" required /></div>
                <div><Label className="text-white">Horário Início</Label><Input type="time" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white" required /></div>
                <div><Label className="text-white">Horário Término</Label><Input type="time" value={eventForm.end_time} onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white" /></div>
              </div>

              {eventForm.event_type !== "online" && (
                <div>
                  <Label className="text-white">Local do Evento</Label>
                <select value={eventForm.venue_id} onChange={(e) => handleVenueChange(e.target.value)} className="w-full bg-zinc-900/80 border border-white/10 text-white rounded-md px-3 py-2">
                  <option value="">Selecione um local...</option>
                  {venues.map((venue) => (<option key={venue.id} value={venue.id}>{venue.name} {venue.capacity ? `(${venue.capacity} lugares)` : ""}</option>))}
                </select>
                <p className="text-zinc-500 text-xs mt-1">
                  {"Não encontrou?"}{" "}
                  <button
                    type="button"
                    onClick={() => {
                      onOpenChange(false);
                      document.querySelector('[data-testid="tab-venues"]')?.click();
                    }}
                    className="text-[#fccf1f] hover:underline"
                  >
                    Cadastre um novo local
                  </button>
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-white">Ingressos Disponíveis</Label><Input type="number" value={eventForm.tickets_available} onChange={(e) => setEventForm({ ...eventForm, tickets_available: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white" /></div>
                <div><Label className="text-white">URL da Imagem</Label><Input type="url" value={eventForm.image_url} onChange={(e) => setEventForm({ ...eventForm, image_url: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white" /></div>
              </div>
              <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                <Label className="text-white font-semibold">Premiação</Label>
                <textarea
                  value={eventForm.prize_description || ""}
                  onChange={(e) => setEventForm({ ...eventForm, prize_description: e.target.value })}
                  className="w-full bg-zinc-900/80 border border-white/10 text-white rounded-md px-3 py-2 min-h-[80px]"
                  placeholder="Ex: 1º lugar: Vale R$100, 2º lugar: Cerveja grátis..."
                />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={eventForm.copy_last_prize}
                    onChange={(e) => handleCopyLastPrizeChange(e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#fccf1f] focus:ring-[#fccf1f]"
                    disabled={!eventForm.venue_id}
                  />
                  <span className={`text-sm ${eventForm.venue_id ? "text-zinc-300" : "text-zinc-500"}`}>
                    Repetir premiação do último evento neste local
                  </span>
                </label>
                {!eventForm.venue_id && <p className="text-zinc-500 text-xs">Selecione um local primeiro para copiar a premiação</p>}
              </div>

              <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={eventForm.is_recurring}
                    onChange={(e) => setEventForm({ ...eventForm, is_recurring: e.target.checked })}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#fccf1f] focus:ring-[#fccf1f]"
                    disabled={!!editingEvent}
                  />
                  <span className="text-white font-semibold">Evento Recorrente</span>
                </label>
                {editingEvent && eventForm.series_id && <p className="text-zinc-400 text-xs">Este evento faz parte de uma série. Edite apenas esta ocorrência.</p>}
                {eventForm.is_recurring && !editingEvent && (
                  <div className="grid md:grid-cols-3 gap-3 mt-3">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-400 text-sm whitespace-nowrap">Repetir a cada</span>
                      <Input type="number" min="1" value={eventForm.recurrence_interval} onChange={(e) => setEventForm({ ...eventForm, recurrence_interval: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white w-20" />
                    </div>
                    <select value={eventForm.recurrence_unit} onChange={(e) => setEventForm({ ...eventForm, recurrence_unit: e.target.value })} className="bg-zinc-900/80 border border-white/10 text-white rounded-md px-3 py-2">
                      <option value="days">dias</option>
                      <option value="weeks">semanas</option>
                      <option value="months">meses</option>
                    </select>
                    <div>
                      <Input type="date" value={eventForm.recurrence_end_date} onChange={(e) => setEventForm({ ...eventForm, recurrence_end_date: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white" />
                      <p className="text-zinc-500 text-xs mt-1">Data final</p>
                    </div>
                  </div>
                )}
              </div>

              {eventForm.event_type === "online" && (
                <>
                  {(!eventForm.is_streaming || eventForm.streaming_mode !== "embedded_room") && (
                    <div className="rounded-lg border border-dashed border-[#fccf1f]/25 bg-[#fccf1f]/5 p-4">
                      <p className="text-sm font-semibold text-[#fccf1f]">Integração com quizzes do Quiz Maker</p>
                      <p className="mt-1 text-xs text-zinc-400">
                        Para vincular um quiz fechado do Quiz Maker a este evento, ative <strong>Ao vivo / Streaming</strong> e selecione <strong>Jogo incorporado no site</strong>.
                      </p>
                    </div>
                  )}
                  <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                    <Label className="text-white font-semibold">Jogadores Liberados</Label>
                    <p className="text-zinc-500 text-xs">Estes planos entram sem comprar ingresso. Os demais planos habilitados poderao jogar, mas pagam ingresso quando houver cobranca.</p>
                    <div className="flex flex-wrap gap-2">
                      {[{ value: "free", label: "Jogador Casual" }, { value: "pro", label: "Quizzeiro Pro" }, { value: "master", label: "Lorde Supremo" }].map((plan) => (
                        <button
                          key={plan.value}
                          type="button"
                          onClick={() => handleFreePlanToggle(plan.value)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${(eventForm.free_plans || []).includes(plan.value) ? "bg-green-500/30 text-green-400 border border-green-500/50" : "bg-zinc-700/50 text-zinc-400 border border-zinc-600 hover:border-green-500/30"}`}
                        >
                          {plan.label}
                        </button>
                      ))}
                    </div>
                    {!eventForm.requires_game_pass ? (
                      <p className="text-zinc-600 text-xs">Como este jogo está sem cobrança por passe, essa lista só entra em ação se você ativar a cobrança depois.</p>
                    ) : null}
                    {eventForm.requires_game_pass ? (
                      <label className="mt-3 flex cursor-pointer items-center gap-2 rounded-lg border border-amber-400/20 bg-amber-400/5 px-3 py-2">
                        <input type="checkbox" checked={eventForm.ticket_sales_paused === true} onChange={(e) => setEventForm({ ...eventForm, ticket_sales_paused: e.target.checked })} className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-[#fccf1f] focus:ring-[#fccf1f]" />
                        <span className="text-sm text-zinc-200">Pausar temporariamente a venda de Passes de Jogo</span>
                      </label>
                    ) : null}
                    <label className="mt-3 flex cursor-pointer items-center gap-2 rounded-lg border border-sky-400/20 bg-sky-400/5 px-3 py-2">
                      <input type="checkbox" checked={eventForm.requires_game_pass === true} onChange={(e) => setEventForm({ ...eventForm, requires_game_pass: e.target.checked })} className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-[#fccf1f] focus:ring-[#fccf1f]" />
                      <span className="text-sm text-zinc-200">Exigir Passes de Jogo para acessar este jogo</span>
                    </label>
                    {eventForm.requires_game_pass ? (
                      <div className="mt-3 max-w-xs">
                        <Label className="text-zinc-300 text-sm">Passes necessários</Label>
                        <Input type="number" min="1" max="100" value={eventForm.game_pass_cost} onChange={(e) => setEventForm({ ...eventForm, game_pass_cost: e.target.value })} className="mt-1 bg-zinc-900/80 border-white/10 text-white" />
                        <p className="mt-1 text-xs text-zinc-500">Este valor será debitado uma única vez no primeiro desbloqueio.</p>
                      </div>
                    ) : null}
                  </div>

                  {!eventForm.is_streaming && (
                    <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                      <Label className="text-white font-semibold">Período Disponível para Jogar</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-sm">Início</Label>
                          <Input type="datetime-local" value={eventForm.online_start_datetime} onChange={(e) => setEventForm({ ...eventForm, online_start_datetime: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white" required={!eventForm.is_streaming} />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">Término</Label>
                          <Input type="datetime-local" value={eventForm.online_end_datetime} onChange={(e) => setEventForm({ ...eventForm, online_end_datetime: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white" required={!eventForm.is_streaming} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={eventForm.is_streaming} onChange={(e) => handleEventFormChange({ is_streaming: e.target.checked })} className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#fccf1f] focus:ring-[#fccf1f]" />
                      <span className="text-white font-semibold">Ao vivo / Streaming</span>
                    </label>
                    {eventForm.is_streaming && (
                      <>
                        <div>
                          <Label className="text-zinc-400 text-sm">Como o público vai assistir</Label>
                          <select
                            value={eventForm.streaming_mode || "external_link"}
                            onChange={(e) => setEventForm({ ...eventForm, streaming_mode: e.target.value })}
                            className="w-full bg-zinc-900/80 border border-white/10 text-white rounded-md px-3 py-2"
                          >
                            <option value="external_link">Link externo</option>
                            <option value="embedded_room">Jogo incorporado no site</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">
                            {eventForm.streaming_mode === "embedded_room" ? "Link da transmissão incorporada" : "Link para a Live"}
                          </Label>
                          <Input
                            type="url"
                            value={eventForm.streaming_link}
                            onChange={(e) => setEventForm({ ...eventForm, streaming_link: e.target.value })}
                            className="bg-zinc-900/80 border-white/10 text-white"
                            placeholder="https://youtube.com/live/..."
                          />
                          {eventForm.streaming_mode === "embedded_room" ? (
                            <p className="text-zinc-500 text-xs mt-2">
                              A sala incorporada vai usar este link como vídeo principal. O jogo em tempo real e o chat ficarão na própria página do evento.
                            </p>
                          ) : null}
                        </div>
                        {eventForm.streaming_mode === "embedded_room" ? (
                          <div className="space-y-2 rounded-lg border border-[#fccf1f]/15 bg-black/20 p-4">
                            <div className="rounded-xl border border-sky-500/25 bg-sky-500/10 p-4 space-y-3">
                              <div>
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-300">Contingencia Kahoot</p>
                                <p className="mt-1 text-sm text-zinc-300">
                                  Use este campo quando o jogo for conduzido no Kahoot. O PIN so aparece para usuarios liberados para jogar.
                                </p>
                              </div>
                              <div>
                                <Label className="text-zinc-200 text-sm">PIN do jogo do Kahoot</Label>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  value={eventForm.kahoot_pin || ""}
                                  onChange={(e) => setEventForm({ ...eventForm, kahoot_pin: e.target.value.replace(/\D/g, "").slice(0, 12) })}
                                  className="bg-zinc-900/80 border-white/10 text-white font-mono text-lg tracking-[0.18em]"
                                  placeholder="Ex: 123456"
                                />
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-zinc-200 text-sm">Origem do quiz da sala incorporada</Label>
                              <p className="text-xs text-zinc-500">
                                Para a sala ao vivo nativa, o caminho mais completo e importar um quiz fechado do Quiz Maker.
                              </p>
                            </div>
                            <div className="grid gap-4 lg:grid-cols-2">
                              <div className="rounded-xl border border-[#fccf1f]/20 bg-zinc-950/60 p-4 space-y-2">
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#fccf1f]">Usar quiz fechado do Quiz Maker</p>
                                <Label className="text-zinc-300 text-sm">Meus Quizzes</Label>
                                <select
                                  value={myQuizMakerQuizzes.some((quiz) => quiz.id === eventForm.quiz_maker_quiz_id) ? eventForm.quiz_maker_quiz_id : ""}
                                  onChange={(e) => handleQuizMakerQuizSelect(e.target.value)}
                                  className="w-full bg-zinc-900/80 border border-white/10 text-white rounded-md px-3 py-2"
                                >
                                  <option value="">Selecione um quiz salvo por voce...</option>
                                  {myQuizMakerQuizzes.map((quiz) => (
                                    <option key={quiz.id} value={quiz.id}>
                                      {formatQuizMakerOptionLabel(quiz)}
                                    </option>
                                  ))}
                                </select>
                                <Label className="text-zinc-300 text-sm">Biblioteca Admin</Label>
                                <select
                                  value={adminQuizMakerQuizzes.some((quiz) => quiz.id === eventForm.quiz_maker_quiz_id) ? eventForm.quiz_maker_quiz_id : ""}
                                  onChange={(e) => handleQuizMakerQuizSelect(e.target.value)}
                                  className="w-full bg-zinc-900/80 border border-white/10 text-white rounded-md px-3 py-2"
                                >
                                  <option value="">Ou selecione um quiz da biblioteca...</option>
                                  {adminQuizMakerQuizzes.map((quiz) => (
                                    <option key={quiz.id} value={quiz.id}>
                                      {formatQuizMakerOptionLabel(quiz)}
                                    </option>
                                  ))}
                                </select>
                                {eventForm.quiz_maker_quiz_id ? (
                                  <p className="text-xs text-emerald-300">
                                    Quiz selecionado: {quizMakerQuizzes.find((quiz) => quiz.id === eventForm.quiz_maker_quiz_id)?.title || eventForm.quiz_maker_quiz_title}
                                  </p>
                                ) : (
                                  <p className="text-xs text-zinc-500">
                                    Escolha um quiz pronto para o console do apresentador carregar as perguntas da sala ao vivo.
                                  </p>
                                )}
                              </div>
                              <div className="rounded-xl border border-white/10 bg-zinc-950/40 p-4 space-y-3">
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-300">Gerar quiz aleatorio pelo banco</p>
                                <p className="text-sm text-zinc-400">
                                  O fluxo por categoria, temas, dificuldade e sorteio aleatorio continua disponivel para eventos online fora da sala ao vivo.
                                </p>
                                <p className="text-xs text-zinc-500">
                                  Se a ideia for montar um quiz automatico pelo banco de dados, use o caminho de evento online sem streaming. Para a sala incorporada, seguimos usando um quiz fechado como base.
                                </p>
                              </div>
                            </div>
                            <p className="text-xs text-zinc-400">
                              Voce pode usar tanto quizzes da biblioteca admin quanto quizzes fechados salvos em Meus Quizzes.
                            </p>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>

                  <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                    <Label className="text-white font-semibold">Jogadores Habilitados</Label>
                    <p className="text-zinc-500 text-xs">Selecione quais planos podem participar deste jogo. Quem nao estiver habilitado aqui nao entra nem pagando ingresso.</p>
                    <div className="flex gap-4 flex-wrap">
                      {[{ id: "free", name: "Jogador Casual (Grátis)" }, { id: "pro", name: "Quizzeiro Pro" }, { id: "master", name: "Lorde Supremo" }].map((plan) => (
                        <label key={plan.id} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={eventForm.eligible_plans?.includes(plan.id)} onChange={() => handlePlanToggle(plan.id)} className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#fccf1f] focus:ring-[#fccf1f]" />
                          <span className="text-zinc-300 text-sm">{plan.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                    <Label className="text-white font-semibold">Jogadores Liberados</Label>
                    <p className="text-zinc-500 text-xs">Estes planos entram sem comprar ingresso. Os demais planos habilitados poderao jogar, mas pagam ingresso quando houver cobranca.</p>
                    <div className="flex flex-wrap gap-2">
                      {[{ value: "free", label: "Jogador Casual" }, { value: "pro", label: "Quizzeiro Pro" }, { value: "master", label: "Lorde Supremo" }].map((plan) => (
                        <button
                          key={plan.value}
                          type="button"
                          onClick={() => handleFreePlanToggle(plan.value)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${(eventForm.free_plans || []).includes(plan.value) ? "bg-green-500/30 text-green-400 border border-green-500/50" : "bg-zinc-700/50 text-zinc-400 border border-zinc-600 hover:border-green-500/30"}`}
                        >
                          {plan.label}
                        </button>
                      ))}
                    </div>
                    {!eventForm.requires_game_pass ? (
                      <p className="text-zinc-600 text-xs">Como este jogo está sem cobrança por passe, essa lista só entra em ação se você ativar a cobrança depois.</p>
                    ) : null}
                  </div>

                  {!eventForm.is_streaming && (
                    <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                      <Label className="text-white font-semibold">Categoria principal, categorias editoriais e temas</Label>
                      <div>
                        <Label className="text-zinc-400 text-sm">Categoria</Label>
                        <select value={eventForm.quiz_category_id} onChange={(e) => handleQuizCategoryChange(e.target.value)} className="w-full bg-zinc-900/80 border border-white/10 text-white rounded-md px-3 py-2" required={!eventForm.is_streaming}>
                          <option value="">Selecione uma categoria...</option>
                          {quizCategoriesThemes.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
                        </select>
                      </div>
                      {eventForm.quiz_category_id && (
                        <div>
                          <Label className="text-zinc-400 text-sm">Categorias editoriais</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {availableThemes.map((theme) => (
                              <label key={theme.name} className={`px-3 py-1.5 rounded-lg cursor-pointer text-sm transition-colors ${eventForm.quiz_themes?.includes(theme.name) ? "bg-[#fccf1f] text-black" : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"} ${!theme.available ? "opacity-50" : ""}`}>
                                <input type="checkbox" className="sr-only" checked={eventForm.quiz_themes?.includes(theme.name)} onChange={() => handleThemeToggle(theme.name)} disabled={!theme.available} />
                                {theme.name} ({theme.question_count})
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      {specialThemes.length > 0 && (
                        <div>
                          <Label className="text-zinc-400 text-sm">Temas (da planilha)</Label>
                          <p className="text-zinc-600 text-xs mb-2">Temas especiais como Harry Potter, Marvel, Star Wars, etc.</p>
                          <div className="flex flex-wrap gap-2 mt-2 max-h-48 overflow-y-auto p-2 bg-zinc-800/30 rounded-lg">
                            {specialThemes.map((theme) => (
                              <label key={theme.name} className={`px-3 py-1.5 rounded-lg cursor-pointer text-sm transition-colors ${eventForm.quiz_themes?.includes(theme.name) ? "bg-purple-500 text-white" : "bg-zinc-700 text-zinc-300 hover:bg-purple-500/30"} ${!theme.available ? "opacity-50" : ""}`} title={theme.is_combined ? `Parte de: ${theme.original_theme}` : ""}>
                                <input type="checkbox" className="sr-only" checked={eventForm.quiz_themes?.includes(theme.name)} onChange={() => handleThemeToggle(theme.name)} disabled={!theme.available} />
                                {theme.name} ({theme.question_count})
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      {eventForm.quiz_themes?.length > 0 && <p className="text-zinc-500 text-xs mt-2">Categorias editoriais e temas selecionados: {eventForm.quiz_themes.join(", ")}</p>}
                    </div>
                  )}

                  {!eventForm.is_streaming && (
                    <>
                      <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={eventForm.quiz_use_difficulty_filter}
                            onChange={(e) => setEventForm((prev) => ({
                              ...prev,
                              quiz_use_difficulty_filter: e.target.checked,
                              quiz_difficulties: e.target.checked ? prev.quiz_difficulties : [],
                            }))}
                            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#fccf1f] focus:ring-[#fccf1f]"
                          />
                          <span className="text-white font-semibold">Definir Dificuldade</span>
                        </label>
                        <p className="text-zinc-500 text-xs">
                          Quando desmarcado, qualquer pergunta válida do banco pode entrar no jogo, respeitando tema, categoria e curadoria.
                        </p>
                        {eventForm.quiz_use_difficulty_filter && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {DIFFICULTY_OPTIONS.map((difficulty) => (
                              <button
                                key={difficulty.value}
                                type="button"
                                onClick={() => handleDifficultyToggle(difficulty.value)}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${(eventForm.quiz_difficulties || []).includes(difficulty.value) ? "bg-[#fccf1f] text-black border border-[#fccf1f]" : "bg-zinc-700/50 text-zinc-300 border border-zinc-600 hover:border-[#fccf1f]/40"}`}
                              >
                                {difficulty.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><Label className="text-white">Número de Perguntas</Label><Input type="number" min="5" max="50" value={eventForm.quiz_question_count} onChange={(e) => setEventForm({ ...eventForm, quiz_question_count: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white" /></div>
                        <div><Label className="text-white">Tempo por Pergunta (segundos)</Label><Input type="number" min="10" max="120" value={eventForm.quiz_time_per_question} onChange={(e) => setEventForm({ ...eventForm, quiz_time_per_question: e.target.value })} className="bg-zinc-900/80 border-white/10 text-white" /></div>
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="bg-zinc-800/50 p-4 rounded-lg space-y-3">
                <Label className="text-white font-semibold">Opções de Exibição</Label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={eventForm.show_capacity} onChange={(e) => setEventForm({ ...eventForm, show_capacity: e.target.checked })} className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#fccf1f] focus:ring-[#fccf1f]" />
                  <span className="text-zinc-300 text-sm">Exibir lotação e quantidade disponível de ingressos publicamente</span>
                </label>
              </div>

              {!isDigitalProduct && <div className="bg-zinc-800/50 p-4 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-white font-semibold flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-[#fccf1f]" />
                    Tipos de Ingresso
                  </Label>
                  <Button type="button" onClick={() => setShowCreateTicketModal(true)} size="sm" variant="outline" className="border-[#fccf1f]/30 text-[#fccf1f] hover:bg-[#fccf1f]/10">
                    <Plus size={14} className="mr-1" /> Criar Ingresso
                  </Button>
                </div>
                <p className="text-zinc-400 text-sm">Selecione os tipos de ingresso disponíveis para este evento.</p>
                <div className="space-y-2">
                  {ticketCatalog.filter((ticketType) => ticketType.is_active !== false).map(renderCatalogTicketEditor)}
                </div>
              </div>}

              {editingEvent && isEventCancelled(editingEvent) ? (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm font-semibold text-red-200">
                  Este evento está cancelado.
                </div>
              ) : null}

              <div className="flex flex-col gap-3 sm:flex-row">
                {editingEvent && !isEventCancelled(editingEvent) ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={saving || cancelling}
                    onClick={handleCancelEvent}
                    className="border-red-500/50 text-red-300 hover:bg-red-500/10"
                  >
                    <Ban className="mr-2 h-4 w-4" />
                    {cancelling ? "Cancelando..." : "Cancelar Evento"}
                  </Button>
                ) : null}
                <Button type="submit" disabled={saving || cancelling} className="flex-1 bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c] rounded-lg py-3">
                  {saving ? "Salvando..." : editingEvent ? "Salvar Alterações" : "Criar Evento"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateTicketModal} onOpenChange={(openState) => { setShowCreateTicketModal(openState); if (!openState) fetchTicketCatalog(); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#fccf1f]" />
              Criar Novo Tipo de Ingresso
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Crie um novo tipo de ingresso. Escolha se deseja salvá-lo para eventos futuros.
            </DialogDescription>
          </DialogHeader>
          <TicketCatalogManager readOnly={false} embedded={true} onClose={() => setShowCreateTicketModal(false)} onLocalCreate={handleLocalTicketCreate} />
        </DialogContent>
      </Dialog>
    </>
  );
}


